// Two-factor authentication settings: re-register (rotate) the authenticator app.
//
// MFA is mandatory and first enrolled during the one-time MFA_SETUP challenge at sign-in,
// so the setup QR is normally never seen again. This card lets an already-signed-in user
// register a new authenticator (e.g. after switching phones) by associating a fresh
// software-token secret, showing a new QR + setup key, and verifying a code from it.

import { useState } from "react";

import { TotpQrCode } from "../components/TotpQrCode";
import { currentEmail, reassociateTotp, type TotpReassociation } from "../auth/cognito";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export function TwoFactorSettings() {
  const [pending, setPending] = useState<TotpReassociation | null>(null);
  const [code, setCode] = useState("");
  const [status, setStatus] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const start = async () => {
    setStatus("");
    setDone(false);
    setBusy(true);
    try {
      const email = (await currentEmail()) ?? "account";
      setPending(await reassociateTotp(email));
    } catch (err) {
      setStatus(err instanceof Error ? err.message : "Could not start re-registration");
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    setStatus("");
    const trimmed = code.trim();
    if (!/^\d{6}$/.test(trimmed)) {
      setStatus("Enter the 6-digit code from your authenticator app.");
      return;
    }
    setBusy(true);
    try {
      await pending!.verify(trimmed);
      setPending(null);
      setCode("");
      setDone(true);
    } catch (err) {
      setStatus(err instanceof Error ? err.message : "Could not verify the code");
    } finally {
      setBusy(false);
    }
  };

  const cancel = () => {
    setPending(null);
    setCode("");
    setStatus("");
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Two-factor authentication</CardTitle>
        <CardDescription>
          Replace the authenticator used for sign-in.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!pending && (
          <>
            <Button
              type="button"
              variant="outline"
              disabled={busy}
              onClick={() => void start()}
              data-testid="reregister-totp"
            >
              {busy ? "Starting…" : "Replace authenticator"}
            </Button>
            {done && (
              <p className="status text-sm text-muted-foreground">
                New authenticator registered. Use it the next time you sign in.
              </p>
            )}
          </>
        )}

        {pending && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Scan this QR code with your authenticator app, then enter the 6-digit code to
              confirm. This replaces your previous authenticator.
            </p>
            <TotpQrCode uri={pending.otpauthUri} />
            <div className="space-y-2">
              <Label htmlFor="reassoc-secret">Setup key</Label>
              <Input
                id="reassoc-secret"
                readOnly
                value={pending.secretCode}
                aria-label="reassoc-secret"
                className="font-mono text-xs"
                onFocus={(e) => e.currentTarget.select()}
              />
              <p className="text-xs text-muted-foreground">
                Can't scan? Enter this key in your authenticator app manually.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="reassoc-code">Authenticator code</Label>
              <Input
                id="reassoc-code"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                aria-label="reassoc-code"
                placeholder="123456"
              />
            </div>
            <div className="flex gap-2">
              <Button type="button" disabled={busy} onClick={() => void verify()}>
                {busy ? "Verifying…" : "Verify and replace"}
              </Button>
              <Button type="button" variant="ghost" disabled={busy} onClick={cancel}>
                Cancel
              </Button>
            </div>
          </div>
        )}

        {status && <p className="status text-sm text-destructive">{status}</p>}
      </CardContent>
    </Card>
  );
}
